import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  SENSOR_ASSIGNMENTS_CREATE,
  SENSOR_ASSIGNMENTS_UPDATE,
  SENSOR_ASSIGNMENTS_VIEW,
} from "../../sensorAssignmentsPermissions";
import type { SensorAssignmentsRouteFactory } from "./sensorAssignmentsRouteContext";
import { sensorAssignmentsSearchSchema } from "./searchSchemas";

const SensorsListScreen = lazyRouteComponent(
  () => import("../SensorsListScreen"),
  "SensorsListScreen",
);
const AssignSensorWizardScreen = lazyRouteComponent(
  () => import("../AssignSensorWizardScreen"),
  "AssignSensorWizardScreen",
);
const SensorDetailScreen = lazyRouteComponent(
  () => import("../SensorDetailScreen"),
  "SensorDetailScreen",
);
const EditSensorScreen = lazyRouteComponent(
  () => import("../EditSensorScreen"),
  "EditSensorScreen",
);

export const createSensorAssignmentsListRoutes: SensorAssignmentsRouteFactory =
  ({ context, sensorAssignments, companies, devices, branches }) => {
    const basePath = context.basePath;

    // '$id/edit' is registered before the bare '$id' route below (same
    // convention as devices' editRoute/detailRoute ordering).
    const editRoute = createRoute({
      getParentRoute: context.getParentRoute,
      path: `${basePath}/$id/edit`,
      beforeLoad: requirePermission([SENSOR_ASSIGNMENTS_UPDATE]),
      component: function EditSensorRouteScreen() {
        const { id } = editRoute.useParams() as unknown as { id: string };
        return createElement(EditSensorScreen, {
          repository: sensorAssignments,
          basePath,
          sensorId: id,
        });
      },
    });

    // '$id' is the most general path here -- kept last, and after the
    // literal 'create' segment above, so it's never captured as a sensor id
    // (same convention as devices/assetTypes).
    const detailRoute = createRoute({
      getParentRoute: context.getParentRoute,
      path: `${basePath}/$id`,
      beforeLoad: requirePermission([SENSOR_ASSIGNMENTS_VIEW]),
      component: function SensorDetailRouteScreen() {
        const { id } = detailRoute.useParams() as unknown as { id: string };
        return createElement(SensorDetailScreen, {
          repository: sensorAssignments,
          basePath,
          sensorId: id,
        });
      },
    });

    return [
      createRoute({
        getParentRoute: context.getParentRoute,
        path: basePath,
        validateSearch: sensorAssignmentsSearchSchema,
        beforeLoad: requirePermission([SENSOR_ASSIGNMENTS_VIEW]),
        component: () =>
          createElement(SensorsListScreen, {
            repository: sensorAssignments,
            basePath,
          }),
      }),
      // 'create' is a literal segment, registered before '$id' so it's
      // never captured as a sensor id (same convention as devices/assetTypes).
      createRoute({
        getParentRoute: context.getParentRoute,
        path: `${basePath}/create`,
        beforeLoad: requirePermission([SENSOR_ASSIGNMENTS_CREATE]),
        component: () =>
          createElement(AssignSensorWizardScreen, {
            repository: sensorAssignments,
            basePath,
            companies,
            devices,
            branches,
          }),
      }),
      editRoute,
      detailRoute,
    ];
  };
